// packages/ui/src/components/chat/ChatWidgetShell.tsx
//
// The shared chrome for EVERY floating chat launcher in the product — the embed
// website widget, the public marketing-site support widget, and the backoffice
// support widget. It owns the structure those three surfaces had copy-pasted (and
// silently drifted on): the fixed-position container, the Grow→Paper panel, the
// primary-coloured header with title/presence/close, and the launcher Fab with its
// optional unread Badge. The app-specific body (forms, conversation panes, home
// screen, …) is passed as `children`; everything that varies between surfaces is a
// prop.
//
// Browser-safe by design (MUI + React + types only — no `next/*`, no
// `useTranslations`) so embed's CRA bundle can consume it; all user-facing text is
// passed in already-resolved (the caller owns its own translator).
'use client';

import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import Fab from '@mui/material/Fab';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import type { DraggableFab } from './useDraggableFab';

export interface ChatWidgetShellProps {
  /** Where the launcher sits + which way the panel opens. Draggable surfaces
   *  spread the `useDraggableFab` result; a static surface passes a fixed corner,
   *  e.g. `{ containerPos: { right: 16, bottom: 16 }, side: 'right', vertical: 'up' }`. */
  geometry: Pick<DraggableFab, 'containerPos' | 'side' | 'vertical'>;
  /** Stacking level of the whole widget. embed lifts this sky-high to clear
   *  partner-site chrome; in-app surfaces sit just above their own drawer. */
  zIndex: number | ((theme: Theme) => number);
  /** Force the chrome to a fixed writing direction regardless of the host page
   *  (embed sets `'ltr'` so RTL partner sites don't flip the widget). */
  dir?: 'ltr' | 'rtl';

  /** Whether the panel is expanded. Controlled by the caller. */
  open: boolean;
  /** Click on the launcher Fab. Draggable surfaces wrap their toggle in `guardClick`. */
  onLauncherClick: () => void;
  /** The header's close (X) button. */
  onClose: () => void;

  /** Header title — rendered host-theme-safe (as a `<div>`, colour inherited) so a
   *  partner site's `h1..h6` rules can never restyle it. */
  title: ReactNode;
  /** MUI Typography variant for the title. Defaults to `'subtitle1'`. */
  titleVariant?: 'subtitle1' | 'subtitle2';
  /** Optional element left of the title (e.g. the backoffice "back" arrow). */
  headerStart?: ReactNode;
  /** Optional presence chip/dot under the title. */
  presence?: ReactNode;
  /** aria-label for the close button. */
  closeLabel: string;
  /**
   * The ground and text of the header band and of the launcher, when a surface gives its own: the embed widget's
   * are the business's brand bar, the colours of the shop's control bar (docs/brand-colours.md phase 4, and the
   * launcher since 2026-11-03). Absent: the theme's primary and its contrast text, for both.
   */
  bandColors?: { bg: string; color: string };

  /** aria-label for the launcher Fab. */
  launcherLabel: string;
  /** When provided, the launcher is wrapped in an error Badge showing this count —
   *  with the z-index override that keeps the count ON TOP of the Fab. (MUI's Fab
   *  sits at `theme.zIndex.fab`; the badge defaults to only `z-index: 1`, so without
   *  this it renders behind the icon.) Omit on surfaces that show no unread count. */
  unread?: number;
  /** Optional element rendered INSIDE the Fab, after the icon (e.g. the backoffice
   *  "support online" dot, which must paint on top of the icon). */
  launcherChild?: ReactNode;
  /** Optional element rendered as a SIBLING of the Fab inside the fixed launcher
   *  container — so it travels with the (draggable) Fab and positions itself
   *  relative to it (e.g. the backoffice "Feedback" tab poking out beneath/above
   *  the button). Position it absolutely; the container's box is exactly the Fab. */
  launcherAccessory?: ReactNode;
  /** MUI Fab size. Defaults to `'medium'` — the product-wide launcher size that the
   *  drag-clamp math in `useDraggableFab` assumes. */
  fabSize?: 'small' | 'medium' | 'large';
  /** Pointer handlers (onPointerDown/Move/Up) for draggable surfaces; omit otherwise. */
  fabProps?: DraggableFab['fabProps'];
  /** Extra Fab styles — drag cursor, `overflow: 'visible'` for a poking-out dot, etc. */
  fabSx?: SxProps<Theme>;

  /** Override the panel's box dimensions (width/height/maxHeight/…). Merged over the
   *  shell's defaults (370×560, capped to the viewport). */
  panelSx?: SxProps<Theme>;

  /** The panel body — the app-specific conversation / form / home UI. */
  children: ReactNode;
}

/**
 * Single source of truth for the floating chat-launcher chrome. See the file
 * header for which surfaces consume it and why.
 */
export function ChatWidgetShell({
  geometry,
  zIndex,
  dir,
  open,
  onLauncherClick,
  onClose,
  title,
  titleVariant = 'subtitle1',
  headerStart,
  presence,
  closeLabel,
  bandColors,
  launcherLabel,
  unread,
  launcherChild,
  launcherAccessory,
  fabSize = 'medium',
  fabProps,
  fabSx,
  panelSx,
  children,
}: ChatWidgetShellProps) {
  const { containerPos, side, vertical } = geometry;
  // Grow the panel out of the corner nearest the launcher (a GPU-cheap
  // transform+opacity, not a height reflow) so it pops up smoothly.
  const panelTransformOrigin = `${vertical === 'up' ? 'bottom' : 'top'} ${side === 'left' ? 'left' : 'right'}`;

  // The band's colours on the launcher, hovered as MUI hovers a primary Fab (its `dark` shade).
  const launcherColorsSx: SxProps<Theme> =
    bandColors === undefined
      ? {}
      : {
          bgcolor: bandColors.bg,
          color: bandColors.color,
          '&:hover': {
            bgcolor: (theme: Theme) => theme.palette.augmentColor({ color: { main: bandColors.bg } }).dark,
            '@media (hover: none)': { bgcolor: bandColors.bg },
          },
        };

  const launcher = (
    <Fab
      color="primary"
      size={fabSize}
      aria-label={launcherLabel}
      onClick={onLauncherClick}
      {...fabProps}
      sx={[
        launcherColorsSx,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        ...(Array.isArray(fabSx) ? fabSx : fabSx ? [fabSx] : []),
      ] as SxProps<Theme>}
    >
      {open ? <CloseIcon /> : <ChatBubbleOutlineIcon />}
      {launcherChild}
    </Fab>
  );

  return (
    // `data-tt-chat-launcher` marks the whole floating widget (launcher + panel +
    // any accessory) so a page screenshot can exclude it from the capture.
    <Box data-tt-chat-launcher="true" dir={dir} sx={{ position: 'fixed', ...containerPos, zIndex }}>
      {/* The panel floats off the launcher (its drag anchor), opening toward
          whichever side/direction keeps it on-screen. */}
      <Grow in={open} timeout={220} unmountOnExit style={{ transformOrigin: panelTransformOrigin }}>
        <Paper
          elevation={8}
          sx={[
            {
              position: 'absolute',
              ...(side === 'left' ? { left: 0 } : { right: 0 }),
              ...(vertical === 'up' ? { bottom: 'calc(100% + 12px)' } : { top: 'calc(100% + 12px)' }),
              width: 370,
              maxWidth: 'calc(100vw - 32px)',
              height: 560,
              maxHeight: 'calc(100vh - 120px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: 2,
            },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            ...(Array.isArray(panelSx) ? panelSx : panelSx ? [panelSx] : []),
          ] as SxProps<Theme>}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.25,
              bgcolor: bandColors?.bg ?? 'primary.main',
              color: bandColors?.color ?? 'primary.contrastText',
            }}
          >
            {headerStart}
            <Box
              sx={{
                minWidth: 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                alignItems: 'flex-start',
              }}
            >
              {/* component="div": MUI's default variantMapping renders subtitle1/2 as
                  an <h6>, and host themes (Sydney, Astra, …) style h1..h6 with their
                  own colour, overriding our header text. This is widget chrome, not a
                  document heading — render it as a <div> so no host heading rule
                  matches, and pin colour to inherit (the parent Box sets contrastText). */}
              <Typography
                variant={titleVariant}
                component="div"
                sx={{ fontWeight: 600, lineHeight: 1.2, color: 'inherit' }}
              >
                {title}
              </Typography>
              {presence}
            </Box>
            <IconButton size="small" onClick={onClose} aria-label={closeLabel} sx={{ color: 'inherit' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {children}
        </Paper>
      </Grow>

      {/* Launcher — optionally badged with the unread count (z-index lifted above
          the Fab so the count never hides behind the icon). */}
      {unread === undefined ? (
        launcher
      ) : (
        <Badge
          color="error"
          badgeContent={unread}
          overlap="circular"
          sx={{ '& .MuiBadge-badge': { zIndex: (theme) => theme.zIndex.fab + 1 } }}
        >
          {launcher}
        </Badge>
      )}

      {/* An accessory poking out of the launcher (e.g. the Feedback tab). It sits
          in the same fixed container as the Fab, so it follows a drag; the caller
          positions it absolutely relative to the Fab. */}
      {launcherAccessory}
    </Box>
  );
}
